import { ChangeDetectionStrategy, Component, HostBinding, inject, signal } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { TranslocoModule } from '@jsverse/transloco';
import { IsTranslationObjectPipe, RmaTranslationObject } from '@rma/generic/util/translation-object';
import { StripNewLinePipe } from '@rma/public/generic/orc/translation';
import { CalloutComponent } from '@rma/public/generic/ui/callout';
import { LoadingIndicatorDirective } from '@rma/public/generic/ui/loading-indicator';
import { getServerError } from '@rma/public/generic/util/server-api-validation';
import { finalize, Observable, take } from 'rxjs';

export interface ConfirmActionData<T = unknown> {
  title?: string;
  buttonText: string;
  message?: string | RmaTranslationObject;
  error?: string;
  dataId?: string;
  useTranslation?: boolean;
  severity?: 'primary' | 'error';
  cancelButtonText?: string;
  onConfirm: () => Observable<T>;
}

@Component({
  selector: 'rma-confirm-action',
  templateUrl: './confirm-action.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CalloutComponent,
    IsTranslationObjectPipe,
    LoadingIndicatorDirective,
    MatButtonModule,
    MatDialogModule,
    StripNewLinePipe,
    TranslocoModule,
  ],
})
export class ConfirmActionComponent {
  public readonly data = inject<ConfirmActionData>(MAT_DIALOG_DATA);
  public readonly dialogRef = inject<MatDialogRef<ConfirmActionComponent>>(MatDialogRef);

  @HostBinding('attr.data-id')
  public dataId: string | undefined;

  protected readonly error = signal('');
  protected readonly showLoading = signal(false);
  protected readonly useTranslation: boolean = false;

  public constructor() {
    if (this.data.dataId) {
      this.dataId = this.data.dataId;
    }
    if (this.data.useTranslation) {
      this.useTranslation = this.data.useTranslation;
    }
  }

  public close(result = false): void {
    if (!result) {
      this.dialogRef.close(false);
      return;
    }
    this.showLoading.set(true);
    this.data
      .onConfirm()
      .pipe(
        take(1),
        finalize(() => {
          this.showLoading.set(false);
        }),
      )
      .subscribe({
        next: (res) => this.dialogRef.close(res),
        error: (errorResponse) => this.error.set(getServerError(errorResponse).message ?? 'An error occurred, please try again.'),
      });
  }
}
